import React, { useEffect, useState } from "react";
import writeXlsxFile from "write-excel-file";
import AlertPop from "./AlertPop";
import {
  dateForExcel,
  excelFileName,
  dateString,
  disclaimer,
  getMonth,
  UNITTYPES,
} from "./Utils";

const DataExport = ({
  currSymbol,
  currRate,
  marketName,
  monthlyAvgPrice,
  unitTypes,
  unitType,
  rate,
  currency,
  lastMonAvg,
  lowPriceAvg,
  highPriceAvg,
  tableData,
  unitAvailable,
  exportClick,
}) => {
  const [filterData, setFilterData] = useState([]);
  const [areUnitsAvailable, setAreUnitsAvailable] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const date = new Date();
  const rateText = rate === "daily" ? "Nightly" : "Monthly";
  const unitTypesLen = unitTypes.length;
  const unitPrice = (rate) => {
    return rate !== null ? Math.round(rate * currRate) : "N/A";
  };

  useEffect(() => {
    if (tableData) {
      const filDataObj = Object.keys(tableData)
        ?.reverse()
        ?.reduce((acc, curr) => {
          let units = tableData[curr].filter((tt) =>
            unitTypes.toString().includes(tt.aptType)
          );
          return [...acc, ...units];
        }, []);

      const filData = filDataObj.filter(
        (value) => Object.keys(value).length !== 0
      );

      const finalData = filData?.map((d, i) => {
        return [
          {
            value: i % unitTypesLen === 0 ? getMonth(d.yearMonth) : null,
            type: String,
            align: "left",
            borderColor: "#000000",
            bottomBorderColor:
              (i + 1) % unitTypesLen === 0 ? "#000000" : "#ffffff",
            topBorderColor: i % unitTypesLen === 0 ? "#000000" : "#ffffff",
          },
          {
            value: "Furnished " + UNITTYPES[d.aptType],
            type: String,
            align: "left",
            color: d.aptType === unitType ? "#753BBD" : "#000000",
            backgroundColor: (i + 1) % 2 === 0 ? "#f6f6f6" : "#ffffff",
            borderColor: "#000000",
          },
          {
            value: `${currSymbol}${unitPrice(d.minRate)}`,
            type: String,
            align: "center",
            backgroundColor: (i + 1) % 2 === 0 ? "#f6f6f6" : "#ffffff",
            borderColor: "#000000",
          },
          {
            value: `${currSymbol}${unitPrice(d.avgRate)}`,
            type: String,
            align: "center",
            fontWeight: d.aptType === unitType ? "bold" : "",
            color: d.aptType === unitType ? "#753BBD" : "#000000",
            fontSize: d.aptType === unitType ? 15 : 14,
            height: 24,
            backgroundColor: (i + 1) % 2 === 0 ? "#f6f6f6" : "#ffffff",
            borderColor: "#000000",
            leftBorderColor: "#753BBD",
            rightBorderColor: "#753BBD",
            bottomBorderColor: i + 1 === filData.length ? "#753BBD" : "#000000",
            leftBorderStyle: "thick",
            rightBorderStyle: "thick",
            bottomBorderStyle: i + 1 === filData.length ? "thick" : null,
          },
          {
            value: `${currSymbol}${unitPrice(d.maxRate)}`,
            type: String,
            align: "center",
            backgroundColor: (i + 1) % 2 === 0 ? "#f6f6f6" : "#ffffff",
            borderColor: "#000000",
          },
        ];
      });
      setFilterData(finalData);
    }
    const currUnits = unitTypes?.reduce((isAvail, unit) => {
      if (unitAvailable?.[unit]) isAvail = true;
      return isAvail;
    }, false);
    setAreUnitsAvailable(currUnits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType, unitTypes, currRate, tableData]);

  const data = [
    [
      {
        value: "Dwellworks Living Market Rates",
        type: String,
        fontSize: 18,
        fontWeight: "bold",
        color: "#753BBD",
        height: 28,
        wrap: false,
      },
    ],
    [
      {
        value: dateForExcel(date),
        type: String,
        fontSize: 12,
        height: 20,
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: "Market Rate for:",
        type: String,
        fontSize: 12,
        height: 20,
      },
    ],
    [
      {
        value: marketName,
        type: String,
        fontSize: 20,
        height: 25,
        fontWeight: "bold",
      },
    ],
    [
      {
        value: monthlyAvgPrice,
        type: String,
        fontSize: 24,
        height: 32,
        fontWeight: "bold",
      },
    ],
    [
      {
        value: "(12-month average)",
        type: String,
        fontSize: 14,
        height: 20,
        fontWeight: "bold",
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: "This market rate example consists of:",
        type: String,
        fontSize: 12,
        height: 20,
      },
    ],
    [
      {
        value: "Unit Type:",
        type: String,
        fontSize: 14,
        height: 20,
      },
      {
        value: UNITTYPES[unitType],
        type: String,
        fontSize: 14,
        height: 20,
        color: "#000000",
      },
    ],
    [
      {
        value: "Price Rate:",
        type: String,
        fontSize: 14,
        height: 20,
      },
      {
        value: rateText,
        type: String,
        fontSize: 14,
        height: 20,
        color: "#000000",
      },
    ],
    [
      {
        value: "Currency:",
        type: String,
        fontSize: 14,
        height: 20,
      },
      {
        value: currency,
        type: String,
        fontSize: 14,
        height: 20,
        color: "#000000",
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: "Last month avg:",
        type: String,
        fontSize: 12,
        height: 20,
      },
      {
        value: lastMonAvg,
        type: String,
        fontSize: 12,
        height: 20,
        color: "#000000",
        align: "right",
      },
    ],
    [
      {
        value: "Low price avg:",
        type: String,
        fontSize: 12,
        height: 20,
      },
      {
        value: lowPriceAvg,
        type: String,
        fontSize: 12,
        height: 20,
        color: "#000000",
        align: "right",
      },
    ],
    [
      {
        value: "High price avg:",
        type: String,
        fontSize: 12,
        height: 20,
      },
      {
        value: highPriceAvg,
        type: String,
        fontSize: 12,
        height: 20,
        color: "#000000",
        align: "right",
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: "Month-by-Month Averages",
        type: String,
        fontSize: 12,
        height: 20,
        fontWeight: "bold",
      },
    ],
    [
      {
        value: "Unit Type:",
        type: String,
        fontSize: 12,
        height: 20,
      },
      {
        value: `Furnished ${UNITTYPES[unitType]}`,
        type: String,
        fontSize: 12,
        height: 20,
        fontWeight: "bold",
        color: "#000000",
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: "Month/Year",
        align: "left",
        backgroundColor: "#f1ebf8",
        borderColor: "#000000",
        bottomBorderColor: "#000000",
      },
      {
        value: "Unit Type",
        align: "left",
        backgroundColor: "#f1ebf8",
        borderColor: "#000000",
      },
      {
        value: "Low Price",
        align: "center",
        backgroundColor: "#f1ebf8",
        borderColor: "#000000",
      },
      {
        value: "Avg. Price",
        fontWeight: "bold",
        align: "center",
        height: 28,
        fontSize: 15,
        backgroundColor: "#dcceee",
        borderColor: "#000000",
        leftBorderColor: "#753BBD",
        rightBorderColor: "#753BBD",
        topBorderColor: "#753BBD",
        leftBorderStyle: "thick",
        rightBorderStyle: "thick",
        topBorderStyle: "thick",
      },
      {
        value: "High Price",
        align: "center",
        backgroundColor: "#f1ebf8",
        borderColor: "#000000",
      },
    ],
    ...filterData,
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: dateString(date),
        type: String,
        fontSize: 12,
        height: 20,
      },
    ],
    [
      {
        value: null,
        height: 20,
      },
    ],
    [
      {
        value: disclaimer,
        type: String,
        fontSize: 12,
        height: 60,
        span: 6,
        wrap: true,
        alignVertical: "top",
      },
    ],
  ];

  const noDataError = () => {
    exportClick();
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 3000);
  };
  const downloadExcel = async () => {
    exportClick();
    return await writeXlsxFile(data, {
      fileName: excelFileName(date),
    });
  };
  return (
    <>
      {showAlert ? (
        <AlertPop variant="danger">
          <strong>
            <i className="fa fa-close alert-icon"></i> Error!{" "}
            {"No Data To Export"}
          </strong>
        </AlertPop>
      ) : (
        ""
      )}
      <button
        className="export-btn"
        onClick={
          areUnitsAvailable || (unitTypes?.length > 1 && filterData.length > 0)
            ? downloadExcel
            : noDataError
        }
      >
        Export <span className="export"></span>
      </button>
    </>
  );
};

export default DataExport;
